import React, { useState, useMemo } from 'react'
import { Box, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useUser } from 'store'
import Sites from './Sites'

import Switch from 'packages/ui/Switch'

import TabPanel from 'components/TabPanel'
import { CardBlockInfo, CardBody } from 'packages/ui/CardPreview'
import useFilters from '../../store'
import { formatDateNative, extractTimeFromDate } from '../../helpers'
import { CATEGORIES_ROLES } from '../../roles-mapping'

const Category = ({ category }) => {
  const { t } = useTranslation()
  const roles = useMemo(() => {
    return [...category.roles].sort((a, b) => t(`auth.admin.role.${a}`).localeCompare(t(`auth.admin.role.${b}`)))
  }, [t, category.roles])

  return (
    <Stack gap={1}>
      <Stack sx={{ fontSize: 13, fontWeight: 400 }}>{t(`auth.admin.role.category.${category.name}`)}</Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={0.5} flexWrap="wrap">
        {roles.map(role => (
          <Tooltip
            key={role}
            title={t(`auth.admin.role.tooltip.${role}`)}
            arrow
            placement={'top'}>
            <Box
              sx={{
                paddingLeft: '0.75rem',
                paddingRight: '0.75rem',
                borderRadius: '0.25rem',
                border: `1px solid ${category.name === 'internal' ? '#D98B06' : '#DCE2E5'}`,
                fontSize: '0.75rem',
                lineHeight: '1.5rem',
                fontWeight: 300
              }}>
              {t(`auth.admin.role.${role}`)}
            </Box>
          </Tooltip>
        ))}
      </Stack>
    </Stack>
  )
}

const CategoryContact = ({contact}) => {
  const role = useMemo(() => {
    return contact.roles.admin_contact && contact.roles.admin_roles
      ? 'admin_roles_and_contact'
      : contact.roles.readonly_contact && contact.roles.admin_contact && !contact.roles.admin_roles
        ? 'readonly_contact'
        : 'no_management'
  }, [contact])

  return (
    <Category category={{name: 'admin_users', roles: [role]}} />
  )
}
const RolesList = ({contact}) => {
  const { t } = useTranslation()
  const categories = useMemo(() => {
    return CATEGORIES_ROLES.map(category => {
      return {
        name: category.name,
        roles: Object.keys(category.roles).filter(role => contact.roles[role])
      }
    }).filter(({ roles }) => roles.length)
  }, [contact])
  return (
    <>
      {contact?.roles && (
        <Stack gap={2}>
          {
            contact.admin
              ? <Stack sx={{fontSize: 13}}>{t('administration.card.disclaimer_admin')}</Stack>
              : categories.map(category => (
                  <Category key={category.name} category={category} />
              ))
          }
          {
            !contact.admin && (
              <CategoryContact contact={contact} />
            )
          }
          {!contact.internal && (
            <Stack direction={'row'} alignItems={'center'} mb={2}>
              <Typography variant="caption" sx={{ textAlign: 'left' }}>
                <Box component="span" sx={{ color: 'black' }} mr={1}>
                  {t('two_fa_disclaimer.title')}
                </Box>
              </Typography>
              <Switch checked={contact.two_fa_activated} disabled />
            </Stack>
          )}
        </Stack>
      )}
    </>
  )
}

const Body = ({ contact }) => {
  const [user] = useUser()
  const { t } = useTranslation()

  const languages = () => {
    if (contact.langue === 'fr_FR') {
      return t('languages.0')
    }
    if (contact.langue === 'en_US') {
      return t('languages.1')
    }
    if (contact.langue === 'de_DE') {
      return t('languages.2')
    }
    if (contact.langue === 'es_ES') {
      return t('languages.3')
    }
    if (contact.langue === 'it_IT') {
      return t('languages.4')
    }
  }

  const country = () => {
    if (contact.country === 'fr' || contact.country === 'FRANCE') {
      return t('country.0')
    }
    if (contact.country === 'en') {
      return t('country.1')
    }
    if (contact.country === 'de') {
      return t('country.2')
    }
    if (contact.country === 'es') {
      return t('country.3')
    }
    if (contact.country === 'it') {
      return t('country.4')
    }
  }

  const createdBy = () => {
    if (!contact.creator) {
      return '-'
    }
    if (user.internal === false && contact.creator.internal === true) {
      return 'WiFirst'
    }
    return `${contact.creator.first_name} ${contact.creator.last_name}`
  }

  const modifiedBy = () => {
    if (!contact.last_modified_by) {
      return '-'
    }

    if (user.internal === false && contact.last_modified_by.internal === true) {
      return 'WiFirst'
    }

    return contact.last_modified_by.label || '-'
  }

  const [selectedTab, setSelectedTab] = useState('roles')
  const { internal } = useFilters()
  const nbRoles = useMemo(() => {
    return CATEGORIES_ROLES.reduce((acc, category) => {
      const nbRoles = Object.keys(category.roles).filter(role => contact.roles[role])
        .length
      return acc + nbRoles
    }, 0)
  }, [contact])

  const onChangeTab = (event, newValue) => {
    setSelectedTab(newValue)
  }

  return (
    <>
      <Tabs
        value={selectedTab}
        onChange={onChangeTab}
        sx={{
          padding: 0,
          fontWeight: 300
        }}>
        <Tab
          component="div"
          label={
            <Stack direction="row" gap={.5} useFlexGap>
              {t('auth.detail.tabs.roles')}
              {
                !contact.admin && nbRoles > 0 && (
                  <Box
                    sx={{
                      padding: '0 0.375rem',
                      backgroundColor: '#f5f5f5',
                      color: '#757575',
                      borderRadius: '0.5rem'
                    }}>
                    {nbRoles}
                  </Box>
                )
              }
            </Stack>
          }
          value={'roles'}
        />
        <Tab component="div" label={t('auth.detail.tabs.infos')} value="infos" />
        {
          !contact.internal && (
            <Tab
              component="div"
              label={
                <Stack direction="row" gap={.5} useFlexGap>
                  {t('auth.detail.tabs.sites')}
                  <Box
                    sx={{
                      padding: '0 0.375rem',
                      backgroundColor: '#f5f5f5',
                      color: '#757575',
                      borderRadius: '0.5rem'
                    }}>
                    {contact.nb_sites}
                  </Box>
                </Stack>
              }
              value="sites"
            />
          )
        }
      </Tabs>

      <TabPanel value={selectedTab} index="infos">
        <CardBody grid>
          {contact.phone && <CardBlockInfo title={t('auth.detail.infos.phone')} subtitle={contact.phone} />}
          <CardBlockInfo title={t('auth.detail.infos.creation_date')} subtitle={formatDateNative(contact.created_at)} />

          <CardBlockInfo
            title={t('auth.detail.infos.created_by')}
            subtitle={createdBy()}
            tooltiptitle={user.internal || !contact.creator?.internal ? contact.creator?.email : null}
          />

          <CardBlockInfo title={t('auth.detail.infos.language')} subtitle={languages()} />
          <CardBlockInfo title={t('auth.detail.infos.country')} subtitle={country()} />
          <CardBlockInfo
            title={t('auth.detail.infos.edit_date')}
            subtitle={formatDateNative(contact.updated_at)}
            tooltiptitle={extractTimeFromDate(contact.updated_at)}
          />
          <CardBlockInfo
            title={t('auth.detail.infos.modified_by', 'Modifié par')}
            subtitle={modifiedBy()}
            tooltiptitle={user.internal || !contact.last_modified_by?.internal ? contact.last_modified_by?.email : null}
          />
          <CardBlockInfo
            title={t('auth.detail.infos.last_connection')}
            subtitle={formatDateNative(contact.last_sign_in_at)}
            tooltiptitle={extractTimeFromDate(contact.last_sign_in_at)}
          />
          <CardBlockInfo title={t('auth.detail.infos.disable_date')} subtitle={formatDateNative(contact.deleted_at)} />
          {
            Boolean(contact.password_locked_at) && (
              <>
                <CardBlockInfo title={t('auth.detail.infos.password_locked_at')} subtitle={formatDateNative(contact.password_locked_at)} />
                <CardBlockInfo title={t('auth.detail.infos.password_lock_reason_title')} subtitle={t(`auth.detail.infos.password_lock_reason_subtitle.${contact.password_lock_reason}`)} />
              </>
            )
          }
        </CardBody>
      </TabPanel>

      <TabPanel value={selectedTab} index="sites">
        {!internal && <Sites contact={contact} />}
      </TabPanel>

      <TabPanel value={selectedTab} index="roles">
        <RolesList contact={contact} />
      </TabPanel>
    </>
  )
}

export default Body
